import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import { useField } from "formik";
import { v4 as uuid } from "uuid";
import styles from "../RuleForm.module.scss";
import { FolderIcon } from "../../../../../icons";
import { FormRuleActionType, TransactionRuleFormSchema } from "../constants";
import { CustomSelect } from "../../../../forms/CustomSelect/CustomSelect";
import { useWorkspaceRuleGroups } from "../../../../../api/rules.api";

interface RuleGroupFormProps {
    isAccounting: boolean;
}

export const RuleGroupForm: React.FC<RuleGroupFormProps> = ({
    isAccounting,
}) => {
    const ruleGroups = useWorkspaceRuleGroups();
    const [{ value: groupId }, , { setValue: setGroupId }] =
        useField<TransactionRuleFormSchema["groupId"]>("groupId");
    const [, , { setValue: setActions }] =
        useField<TransactionRuleFormSchema["actions"]>("actions");

    const onGroupChange = useCallback(
        (selectedGroupId: string) => {
            const currentGroup = ruleGroups.data?.ruleGroups.find(
                (g) => g.id === groupId,
            );
            const selectedGroup = ruleGroups.data?.ruleGroups.find(
                (g) => g.id === selectedGroupId,
            );

            setGroupId(selectedGroupId);

            if (currentGroup?.isAccounting !== selectedGroup?.isAccounting) {
                const newActions = selectedGroup?.isAccounting
                    ? [
                          {
                              id: uuid(),
                              actionType: FormRuleActionType.ACCOUNT_ID,
                          },
                      ]
                    : [{ id: uuid() }];
                setActions(newActions);
            }
        },
        [ruleGroups.data?.ruleGroups, setGroupId, groupId, setActions],
    );

    const groupOptions = useMemo(
        () =>
            ruleGroups.data?.ruleGroups
                .filter((group) => group.isAccounting === isAccounting)
                .map((group) => ({
                    value: group.id,
                    label: group.name,
                })) ?? [],
        [ruleGroups.data?.ruleGroups, isAccounting],
    );

    return (
        <section className={styles.sectionGroup}>
            <header className={styles.header}>
                <aside
                    className={classNames(
                        styles.sectionIcon,
                        styles.sectionIconGroup,
                    )}
                >
                    <FolderIcon className="icon-color-gray-600" />
                </aside>
                <h5>Group</h5>
            </header>

            <main className={styles.content}>
                <div className={classNames(styles.sectionRows, styles.select)}>
                    <CustomSelect
                        className={classNames(
                            styles.select,
                            styles.groupSelect,
                        )}
                        value={groupId ?? undefined}
                        onSelected={onGroupChange}
                        dropdownKey="ruleGroup"
                        placeholder="Select"
                        size="xs"
                        options={groupOptions}
                    />
                </div>
            </main>
        </section>
    );
};
